import { Injectable } from '@angular/core';
import { BaseWebsocketHubClient } from '@client/shared/services/base-websocket-hub-client';
import { StagedFileUploadRequestDto } from '@client/work-packages/interfaces/staged-file.interface';
import { ShareRecipient } from '@privanot/shared-models';
import { BatchSnapshot } from '@client/upload/interfaces/batch-snapshot.interface';
import { UploadWebsocketEvents, UploadWebsocketMethods } from '@client/upload/interfaces/upload-websocket.interface';
import { DocumentStructureResponse } from '@client/work-packages/interfaces/document-upload.interface';
import { RetryPlan } from '@client/upload/interfaces/retry-plan.interface';
import { UploadFolderContentResponse } from '@client/work-packages/interfaces/upload-folder-content.interface';

const UPLOAD_HUB_PATH = 'upload';

@Injectable({ providedIn: 'root' })
export class UploadService extends BaseWebsocketHubClient<UploadWebsocketMethods, UploadWebsocketEvents> {
    protected override hubPath: string = UPLOAD_HUB_PATH;

    public confirmFileSelection(
        projectId: string,
        name: string,
        files: StagedFileUploadRequestDto[],
        recipients: ShareRecipient[] = [],
    ): Promise<BatchSnapshot> {
        return this.invokeMethod('confirmFileSelection', projectId, name, files, recipients);
    }

    public subscribeToBatch(batchId: string): Promise<BatchSnapshot> {
        return this.invokeMethod('subscribeToBatch', batchId);
    }

    public getUploadFolderContents(): Promise<UploadFolderContentResponse[]> {
        return this.invokeMethod('getUploadFolderContents');
    }

    public getDocumentStructure(path: string, archiveEntryFilter: string[]): Promise<DocumentStructureResponse> {
        return this.invokeMethod('getDocumentStructure', path, archiveEntryFilter);
    }

    public getActiveUploadBatch(): Promise<BatchSnapshot | null> {
        return this.invokeMethod('getActiveUploadBatch');
    }

    public dismissUploadBatch(batchId: string): Promise<void> {
        return this.invokeMethod('dismissUploadBatch', batchId);
    }

    public getRetryPlan(operationId: string, operationFileIds: string[]): Promise<RetryPlan> {
        return this.invokeMethod('getRetryPlan', operationId, operationFileIds);
    }

    public retryOperationFiles(
        operationId: string,
        operationFileIds: string[],
        files: StagedFileUploadRequestDto[] | null,
    ): Promise<BatchSnapshot> {
        return this.invokeMethod('retryOperationFiles', operationId, operationFileIds, files);
    }

    public onFileUploadStarted(handler: UploadWebsocketEvents['fileUploadStarted']): () => void {
        return this.on('fileUploadStarted', handler);
    }

    public onFileUploadProgress(handler: UploadWebsocketEvents['fileUploadProgress']): () => void {
        return this.on('fileUploadProgress', handler);
    }

    public onFileUploadCompleted(handler: UploadWebsocketEvents['fileUploadCompleted']): () => void {
        return this.on('fileUploadCompleted', handler);
    }

    public onFileUploadFailed(handler: UploadWebsocketEvents['fileUploadFailed']): () => void {
        return this.on('fileUploadFailed', handler);
    }

    public onBatchCompleted(handler: UploadWebsocketEvents['batchCompleted']): () => void {
        return this.on('batchCompleted', handler);
    }
}
